export type IfiWidgetViewState = "idle" | "submitting" | "success" | "error";

export const IFI_FORM_STEPS = [
  "intro",
  "country",
  "patrimoine-france",
  "actifs",
  "passifs",
  "contact",
  "result",
  "expert-optin",
] as const;
export type IfiFormStep = (typeof IFI_FORM_STEPS)[number];

export interface IfiCountryOption {
  code: string;
  name: string;
}

export interface IfiResultModel {
  imposable: boolean;
  seuil: number;
  net: number;
  montant: number;
  bareme: Array<{
    tranche: string;
    taux: number;
    montant: number;
  }>;
}

export interface IfiFormModel {
  email: string;
  firstName: string;
  source: string;
  country: string;
  consent: boolean;
  residencePrincipale: string;
  autresBiens: string;
  actifs: string;
  passifs: string;
  expertComptable: boolean;
  courtier: boolean;
  chasseurImmobilier: boolean;
  honeypot: string;
}

export function getInitialIfiFormStep(): IfiFormStep {
  return IFI_FORM_STEPS[0];
}

export function getNextIfiFormStep(
  currentStep: IfiFormStep,
  form: IfiFormModel,
  result: IfiResultModel | null,
): IfiFormStep {
  switch (currentStep) {
    case "intro":
      return "country";
    case "country":
      return isFranceCountry(form.country) ? "patrimoine-france" : "actifs";
    case "patrimoine-france":
      return "passifs";
    case "actifs":
      return "passifs";
    case "passifs":
      return "contact";
    case "contact":
      return "result";
    case "result":
      return result?.imposable ? "expert-optin" : "result";
    case "expert-optin":
      return "expert-optin";
    default:
      return currentStep;
  }
}

export function getPreviousIfiFormStep(
  currentStep: IfiFormStep,
  form: IfiFormModel,
  result: IfiResultModel | null,
): IfiFormStep {
  switch (currentStep) {
    case "country":
      return "intro";
    case "patrimoine-france":
    case "actifs":
      return "country";
    case "passifs":
      return isFranceCountry(form.country) ? "patrimoine-france" : "actifs";
    case "contact":
      return "passifs";
    case "result":
      return "contact";
    case "expert-optin":
      return result?.imposable ? "result" : "contact";
    default:
      return currentStep;
  }
}

export function getIfiFormProgress(
  currentStep: IfiFormStep,
  form?: IfiFormModel,
  result?: IfiResultModel | null,
): {
  current: number;
  total: number;
} {
  if (currentStep === "intro") {
    return { current: 0, total: 5 };
  }

  if (currentStep === "expert-optin") {
    return { current: 6, total: 6 };
  }

  if (currentStep === "result") {
    if (result?.imposable) {
      return { current: 5, total: 6 };
    }
    return { current: 5, total: 5 };
  }

  const useFranceFlow = isFranceCountry(form?.country ?? "");
  const progressOrder: IfiFormStep[] = useFranceFlow
    ? ["country", "patrimoine-france", "passifs", "contact"]
    : ["country", "actifs", "passifs", "contact"];
  const index = progressOrder.indexOf(currentStep);

  return {
    current: index >= 0 ? index + 1 : 1,
    total: 5,
  };
}

export function isFranceCountry(value: string): boolean {
  const normalized = value.trim().toUpperCase();
  return normalized === "FR" || normalized === "FRANCE";
}
